import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Text,
  useColorModeValue,
  Flex,
  Skeleton,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  SimpleGrid,
  Tooltip,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { FaBuilding, FaSync } from 'react-icons/fa';
import { useRegionData } from '../../../../contexts/RegionDataContext';

const TahsilSection = ({ onCountUpdate }) => {
  const [searchInput, setSearchInput] = useState('');
  const { tahsils, isLoading, searchTahsils, refreshTahsils, counts } =
    useRegionData();

  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');
  const accentColor = useColorModeValue('teal.500', 'teal.300');
  const gradientStart = useColorModeValue('green.400', 'green.600');
  const gradientEnd = useColorModeValue('teal.500', 'teal.600');

  const filteredTahsils = useMemo(() => {
    if (!searchInput) return tahsils;
    return tahsils.filter((t) =>
      t.name?.toLowerCase().includes(searchInput.toLowerCase()),
    );
  }, [tahsils, searchInput]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (value) {
      searchTahsils(value);
    }
  };

  const handleRefresh = () => {
    refreshTahsils();
    setSearchInput('');
  };

  useEffect(() => {
    if (onCountUpdate && counts.tahsil > 0) {
      onCountUpdate(counts.tahsil);
    }
  }, [counts.tahsil, onCountUpdate]);

  return (
    <Box position="relative">
      <Card
        mb={4}
        borderRadius="lg"
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="6px"
          bgGradient={`linear(to-r, ${gradientStart}, ${gradientEnd})`}
        />

        <CardBody p={4}>
          <Flex align="center" gap={4} mb={6} pt={2}>
            <Box
              p={3}
              borderRadius="xl"
              bg="green.100"
              _dark={{ bg: 'green.900' }}
              position="relative"
              overflow="hidden"
            >
              <Icon as={FaBuilding} boxSize={6} color="green.500" />
            </Box>
            <VStack align="flex-start" spacing={1}>
              <Heading size="md" color={textColor}>
                Tahsils
              </Heading>
              <Text fontSize="sm" color={subtitleColor}>
                {counts.tahsil} administrative tahsils
              </Text>
            </VStack>
          </Flex>

          <Flex
            mb={6}
            gap={4}
            wrap="wrap"
            align="center"
            justify="space-between"
          >
            <InputGroup size="md" flex="1" minW="300px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="green.500" />
              </InputLeftElement>
              <Input
                placeholder="Search tahsils..."
                value={searchInput}
                onChange={handleSearchChange}
                color={textColor}
                borderRadius="lg"
                border="2px solid"
                borderColor={borderColor}
                _focus={{
                  borderColor: 'green.500',
                  boxShadow: '0 0 0 1px rgba(72, 187, 120, 0.6)',
                }}
                _hover={{
                  borderColor: 'green.300',
                }}
              />
            </InputGroup>
            <HStack spacing={3}>
              <Tooltip label="Refresh data">
                <Flex
                  as="button"
                  p={2}
                  borderRadius="full"
                  bg={`${accentColor}10`}
                  onClick={handleRefresh}
                  cursor="pointer"
                  align="center"
                  justify="center"
                  _hover={{ bg: `${accentColor}20` }}
                  transition="all 0.2s"
                >
                  <Icon as={FaSync} color={accentColor} boxSize={4} />
                </Flex>
              </Tooltip>
              <Badge
                colorScheme="teal"
                variant="subtle"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="xs"
              >
                {filteredTahsils.length} results
              </Badge>
            </HStack>
          </Flex>

          <Box height={'60vh'} overflow={'auto'}>
            {isLoading.tahsils ? (
              <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={4} p={6}>
                {Array.from({ length: 12 }).map((_, i) => (
                  <Skeleton key={i} height="120px" borderRadius="lg" />
                ))}
              </SimpleGrid>
            ) : filteredTahsils.length === 0 ? (
              <VStack
                spacing={4}
                p={8}
                textAlign="center"
                borderRadius="lg"
                border="2px dashed"
                borderColor={borderColor}
              >
                <Icon as={FaBuilding} boxSize={12} color="gray.400" />
                <Text color={subtitleColor} fontSize="lg" fontWeight="medium">
                  {tahsils.length === 0
                    ? 'No tahsils found'
                    : 'No matching tahsils found'}
                </Text>
                <Text color={subtitleColor} fontSize="sm">
                  {tahsils.length === 0
                    ? 'Start by adding some tahsils'
                    : 'Try a different search term'}
                </Text>
              </VStack>
            ) : (
              <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={4}>
                {filteredTahsils.map((tahsil, index) => (
                  <Card
                    key={tahsil._id || index}
                    borderRadius="lg"
                    bg={cardBg}
                    border="1px solid"
                    borderColor={borderColor}
                    _hover={{
                      transform: 'translateY(-4px)',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s',
                    }}
                  >
                    <CardBody p={4}>
                      <VStack spacing={3} align="stretch">
                        <Flex align="center" gap={3}>
                          <Box
                            p={2}
                            borderRadius="lg"
                            bg="green.50"
                            _dark={{ bg: 'green.900' }}
                          >
                            <Icon
                              as={FaBuilding}
                              boxSize={5}
                              color="green.500"
                            />
                          </Box>
                          <Box flex="1">
                            <Text
                              fontWeight="bold"
                              color={textColor}
                              fontSize="md"
                              noOfLines={1}
                            >
                              {tahsil.name?.toUpperCase() || 'N/A'}
                            </Text>
                            <Text fontSize="xs" color={subtitleColor}>
                              Tahsil
                            </Text>
                          </Box>
                        </Flex>

                        {tahsil.subdivisionmain?.name && (
                          <Flex align="center" gap={2}>
                            <Icon
                              as={FaBuilding}
                              color="blue.500"
                              boxSize={4}
                            />
                            <Box flex="1">
                              <Text fontSize="xs" color={subtitleColor}>
                                Sub Division
                              </Text>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color={textColor}
                              >
                                {tahsil.subdivisionmain.name}
                              </Text>
                            </Box>
                          </Flex>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </Box>

          <Flex
            justify="space-between"
            align="center"
            mt={6}
            pt={4}
            borderTop="1px solid"
            borderColor={borderColor}
          >
            <Text fontSize="sm" color={subtitleColor}>
              Showing <strong>{filteredTahsils.length}</strong> of{' '}
              <strong>{tahsils.length}</strong> tahsils
            </Text>
            <Text fontSize="xs" color={subtitleColor}>
              Updated:{' '}
              {new Date().toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Text>
          </Flex>
        </CardBody>
      </Card>
    </Box>
  );
};

export default React.memo(TahsilSection);
